import { useQuery } from 'react-query';
import { createPortal } from 'react-dom';
import { useParams, Link } from 'react-router-dom';

import { Button } from '@vimily/bonjoro-ui-components';
import React, { useRef, useState, useMemo, useContext } from 'react';
import { confirmAsync } from '~/App/Components/Modals/Confirm';
import styled from '@emotion/styled';
import colours, { palette } from '~/utils/colours';
import { Label, TextArea, FieldError } from '~/App/Components/Forms';
import Select from '~/App/Components/Select';
import { useForm, Controller } from 'react-hook-form';
import Checkbox from '~/App/Components/Checkbox';
import { ErrorMessage } from '@hookform/error-message';
import FlashContext from '~/App/Components/Flash/Context';
import { CancelSubscriptionPostData, cancelSubscription } from '../api';
import { useMutation } from 'react-query';
import useCurrentUser from '~/react-hooks/use-current-user';

export interface CancelSurveyReasonDocument {
  value: string;
  title: string;
  pause_available: boolean;
}

interface CancelSurveyDocument extends Document {
  data: {
    body?: Array<{
      primary: CancelSurveyReasonDocument;
    }>;
  };
}

export interface CancelSurveyReason extends CancelSurveyReasonDocument {
  label: string;
}

export interface CancelSubscriptionFormData
  extends Omit<CancelSubscriptionPostData, 'reason'> {
  reason: CancelSurveyReason;
}

enum CancelType {
  Pause = 'pause',
  Cancel = 'cancel',
}

const Wrapper = styled.div`
  width: 100%;
  max-width: 650px;
`;

const Title = styled.h2`
  color: ${palette.darkerGrey};
  font-size: 26px;
  margin-top: 0;
  font-weight: bold;
  margin-bottom: 30px;
`;

const Description = styled.p`
  color: ${colours.greyDarkAlt2};
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  padding-bottom: 20px;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

async function getCancelSurvey() {
  const Prismic = (await import('~/App/Services/Prismic')).default;

  return Prismic.getSingle(
    'cancel_survey',
    {}
  ) as unknown as Promise<CancelSurveyDocument>;
}

export const App = () => {
  const { planSlug } = useParams<{ planSlug: string }>();
  const formRef = useRef<HTMLFormElement>(null);
  const [canPauseSubscription, setCanPauseSubscription] = useState(false);
  const [cancelType, setCancelType] = useState(CancelType.Cancel);
  const { team } = useCurrentUser();

  const { data: document } = useQuery('cancel_survey', getCancelSurvey);
  const { createFlash } = useContext(FlashContext);
  const redirect = '/billing';

  const reasons: CancelSurveyReason[] = useMemo(() => {
    return (
      document?.data?.body?.map(({ primary: reason }) => ({
        label: reason.title,
        ...reason,
      })) ?? []
    );
  }, [document]);

  const handleReasonChange = (
    value: CancelSurveyReason,
    callback: (value: CancelSurveyReason) => void
  ) => {
    if (team?.subscription.status === 'paused') {
      return false;
    }

    if (value.pause_available) {
      setCanPauseSubscription(true);
    }

    callback(value);
  };

  const { control, register, handleSubmit, errors } = useForm();

  const validateReason = (data: CancelSurveyReason) => {
    if (data.value == '') {
      return 'Field is required';
    }

    return true;
  };

  function getConfirmArgs() {
    return [
      `Are you sure you want to ${cancelType} your subscription?`,
      `Yes, ${cancelType}`,
      'Not now',
    ];
  }

  const { mutateAsync: cancelMutation, isLoading } = useMutation(
    cancelSubscription,
    {
      onSuccess: () => {
        createFlash({
          id: 'subscription-cancelled',
          title: 'Success',
          message:
            'Subscription successfully cancelled! Redirecting you back to billing.',
        });
        window.location.href = redirect;
      },
    }
  );

  async function handleFormSubmit(data: CancelSubscriptionFormData) {
    const [text, confirmButtonText, cancelButtonText] = getConfirmArgs();
    if (await confirmAsync(text, confirmButtonText, cancelButtonText)) {
      const { reason, ...rest } = data;
      cancelMutation({ reason: reason.value, ...rest, planSlug });
    }
  }

  return (
    <Wrapper>
      <Title>Cancel your subscription</Title>
      <Description>
        Please tell us why you&apos;re cancelling so we can keep striving to get
        better
      </Description>
      <form
        id='cancellation-form'
        onSubmit={handleSubmit(handleFormSubmit)}
        ref={formRef}
      >
        <input type='hidden' ref={register()} name='type' value={cancelType} />

        <FormGroup>
          <Controller
            control={control}
            name='reason'
            defaultValue={{ value: '', label: 'Select a reason' }}
            render={({
              onChange,
              value,
            }: {
              onChange: (value: CancelSurveyReason) => void;
              value: CancelSurveyReason;
            }) => (
              <Select
                defaultValue={value}
                options={reasons}
                onChange={(value: CancelSurveyReason) =>
                  handleReasonChange(value, onChange)
                }
                selected={value}
              />
            )}
            rules={{ validate: validateReason }}
          />
          <ErrorMessage errors={errors} name='reason' as={FieldError} />
        </FormGroup>

        <FormGroup>
          <Label text='Please describe' />
          <TextArea
            placeholder='Any info we should know to make Bonjoro better?'
            name='message'
            ref={register()}
          />
        </FormGroup>

        <FormGroup>
          <Controller
            control={control}
            name='confirm'
            rules={{ required: 'Field is required' }}
            defaultValue={false}
            render={({ onChange, name }) => (
              <Checkbox
                onChange={(event) => onChange(event?.target.checked)}
                name={name}
                label='I understand the perks associated with my Bonjoro subscription will no longer be accessible'
              />
            )}
          />
          <ErrorMessage errors={errors} name='confirm' as={FieldError} />
        </FormGroup>

        <ButtonGroup>
          <Link to={redirect}>
            <Button variant='secondary' className='mr-2'>
              Back
            </Button>
          </Link>
          <Button
            disabled={isLoading}
            onClick={() => setCancelType(CancelType.Cancel)}
          >
            Cancel subscription
          </Button>
          {canPauseSubscription && (
            <>
              <Button
                disabled={isLoading}
                className='mr-2'
                onClick={() => setCancelType(CancelType.Pause)}
              >
                Pause my subscription
              </Button>
              <div className='body-copy' style={{ marginTop: -4 }}>
                If you pause your subscription you can keep using your account
                as normal for free, on us! Your billing will just be paused for
                the next 3 months.
              </div>
            </>
          )}
        </ButtonGroup>
      </form>
    </Wrapper>
  );
};

export default function CancelSubscription() {
  const targetElement = document.getElementById('bonjoro-app') as HTMLElement;

  return createPortal(<App />, targetElement);
}
